import dayjs from 'dayjs'

// Generate time slots for the booking dropdown
export const generateTimeSlots = () => {
  const timeSlots = []
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const formattedHour = hour.toString().padStart(2, '0')
      const formattedMinute = minute.toString().padStart(2, '0')
      timeSlots.push(`${formattedHour}:${formattedMinute}`)
    }
  }
  return timeSlots
}

// Build booking details object
export const buildBookingDetails = (
  patient,
  service,
  bookingDate,
  bookingStart,
  selectedMedicalProfessional,
  authState
) => {
  console.log('Building booking details with:', {
    patient: patient?._id,
    service: service?._id,
    selectedMedicalProfessional: selectedMedicalProfessional?._id,
    authStateStructure: {
      hasUser: !!authState?.agiliteUser,
      hasExtraData: !!authState?.agiliteUser?.extraData,
      hasClinics: !!authState?.agiliteUser?.extraData?.clinics,
      clinicsArray: authState?.agiliteUser?.extraData?.clinics
    }
  })

  // Validate required fields
  if (!patient?._id) {
    console.error('Missing patient ID in booking details')
    return null
  }

  if (!service?._id) {
    console.error('Missing service ID in booking details')
    return null
  }

  if (!selectedMedicalProfessional?._id) {
    console.error('Missing medical professional ID in booking details')
    return null
  }

  // Handle clinic ref more robustly
  let clinicRef = null

  if (authState?.agiliteUser?.extraData?.role?.type === 'admin') {
    clinicRef = selectedMedicalProfessional.extraData.clinics[0]
  } else if (authState?.agiliteUser?.extraData?.clinics?.length > 0) {
    clinicRef = authState.agiliteUser.extraData.clinics[0]
  } else {
    // Try alternative clinic locations in auth state
    clinicRef =
      authState?.agiliteUser?.extraData?.clinic ||
      authState?.agiliteUser?.clinicRef ||
      authState?.agiliteUser?.extraData?.clinicRef

    if (clinicRef) {
    } else {
      // As a last resort, try to get clinic from Redux state
      // This requires access to the store, so let's see if we can import it
      console.warn('❌ NO CLINIC REFERENCE FOUND IN AUTH STATE - THIS MAY CAUSE BOOKING ISSUES')
      console.warn('❌ THE BOOKING MAY FAIL WITHOUT A VALID CLINIC REFERENCE')
      console.warn('❌ IF YOU SEE default-clinic ERROR, IT IS FROM CACHED CODE - HARD REFRESH!')
      // Leave clinicRef as null - let the booking system handle the missing clinic
    }
  }

  const bookingDetails = {
    dateCreated: dayjs().format('YYYY-MM-DD HH:mm:ss'),
    service: service._id,
    medicalProfRef: selectedMedicalProfessional._id,
    userRef: patient._id,
    endTime: dayjs(bookingStart, 'HH:mm').add(service.timeslotInterval, 'minute').format('HH:mm'),
    bookingDate: dayjs(bookingDate).format('YYYY-MM-DD'),
    medicalProf: selectedMedicalProfessional,
    startTime: dayjs(bookingStart, 'HH:mm').format('HH:mm'),
    paymentMethods: ['Cash', 'Medical Aid', 'Card'],
    // Always include clinicRef - use null if we don't have a valid one
    clinicRef: clinicRef || null,
    // Add status field that BookingFinalize expects
    status: {
      checkedIn: false,
      stage: 'pending'
    },
    // Add any other fields the booking system might expect
    paymentMethod: 'Cash', // Default payment method
    chiefComplaint: '', // Will be filled by the finalize form
    isReceptionBooking: true
  }

  return bookingDetails
}

// Block reason options for availability blocking
export const blockReasonOptions = [
  'Scheduled Break (Bathroom/Eat)',
  'Time - Catch Up',
  'Notes - Catch Up',
  'Medical Rep Meeting',
  'Extended Consultation',
  'Procedure (Sutures/IV/Wound)'
]

// Get services available for a medical professional
export const getAvailableServices = (servicesState, selectedMedicalProfessional) => {
  return servicesState.reduce((acc, serv) => {
    if (selectedMedicalProfessional?.extraData?.services?.includes(serv._id)) {
      acc.push(serv)
    }
    return acc
  }, [])
}

// Build patient search query
export const buildPatientSearchQuery = (searchQuery) => {
  let qry = { 'extraData.role.type': 'patient' }

  if (searchQuery) {
    const searchTerms = searchQuery.trim().toLowerCase().split(/\s+/)
    const regexPatterns = searchTerms.map((term) => new RegExp(term, 'i'))

    qry = {
      ...qry,
      $or: [
        {
          $expr: {
            $regexMatch: {
              input: {
                $concat: [{ $toLower: '$firstName' }, ' ', { $toLower: '$lastName' }]
              },
              regex: searchTerms.map((term) => `.*${term}.*`).join(''),
              options: 'i'
            }
          }
        },
        {
          $or: [
            {
              $expr: {
                $regexMatch: {
                  input: { $toLower: '$firstName' },
                  regex: regexPatterns.join('|'),
                  options: 'i'
                }
              }
            },
            {
              $expr: {
                $regexMatch: {
                  input: { $toLower: '$lastName' },
                  regex: regexPatterns.join('|'),
                  options: 'i'
                }
              }
            }
          ]
        },
        { phoneNumber: { $regex: searchQuery, $options: 'i' } },
        { email: { $regex: searchQuery, $options: 'i' } },
        { physicalAddress: { $regex: searchQuery, $options: 'i' } },
        { idNo: { $regex: searchQuery, $options: 'i' } }
      ]
    }
  }

  return qry
}
